import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Pencil, X, ShieldCheck } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

const input = "w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors";

export default function ManagerProfile() {
  const { user, condo, refresh } = useAuth();
  const [f, setF] = useState(null);
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (condo) setF({ ...condo }); }, [condo]);
  if (!f) return null;
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/condominium", {
        name: f.name, cnpj: f.cnpj, cep: f.cep, address: f.address, number: f.number,
        complement: f.complement, neighborhood: f.neighborhood, city: f.city, state: f.state,
      });
      await refresh();
      toast.success("Dados do condomínio atualizados!");
      setConfirm(false); setEditing(false);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const Row = ({ label, value }) => (
    <div className="flex justify-between py-2.5 border-b border-white/5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value || "—"}</span>
    </div>
  );

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Perfil do condomínio</h1>
          <p className="text-sm text-muted-foreground">Dados institucionais do condomínio.</p>
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)} data-testid="condo-edit-btn" className="flex items-center gap-2 rounded-full brand-gradient px-4 py-2 text-sm font-semibold text-white active:scale-95 transition-transform">
            <Pencil className="w-4 h-4" /> Editar
          </button>
        )}
      </div>

      <div className="glass rounded-3xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl brand-gradient flex items-center justify-center"><Building2 className="w-6 h-6 text-white" /></div>
          <div><p className="font-display font-bold">{f.name}</p><p className="text-xs text-muted-foreground">Código: {f.code}</p></div>
        </div>
        {!editing ? (
          <div>
            <Row label="CNPJ" value={f.cnpj} />
            <Row label="CEP" value={f.cep} />
            <Row label="Endereço" value={`${f.address || ""}, ${f.number || ""}`} />
            <Row label="Complemento" value={f.complement} />
            <Row label="Bairro" value={f.neighborhood} />
            <Row label="Cidade / Estado" value={`${f.city || ""} / ${f.state || ""}`} />
            <Row label="Síndico responsável" value={user.name} />
          </div>
        ) : (
          <div className="space-y-3">
            <input value={f.name || ""} onChange={up("name")} data-testid="condo-name" placeholder="Nome do condomínio" className={input} />
            <div className="grid grid-cols-2 gap-3">
              <input value={f.cnpj || ""} onChange={up("cnpj")} data-testid="condo-cnpj" placeholder="CNPJ" className={input} />
              <input value={f.cep || ""} onChange={up("cep")} data-testid="condo-cep" placeholder="CEP" className={input} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input value={f.address || ""} onChange={up("address")} placeholder="Endereço" className={`${input} col-span-2`} data-testid="condo-address" />
              <input value={f.number || ""} onChange={up("number")} placeholder="Número" className={input} data-testid="condo-number" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input value={f.complement || ""} onChange={up("complement")} placeholder="Complemento" className={input} />
              <input value={f.neighborhood || ""} onChange={up("neighborhood")} placeholder="Bairro" className={input} data-testid="condo-neighborhood" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input value={f.city || ""} onChange={up("city")} placeholder="Cidade" className={`${input} col-span-2`} data-testid="condo-city" />
              <input value={f.state || ""} onChange={up("state")} placeholder="UF" className={input} data-testid="condo-state" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => { setEditing(false); setF({ ...condo }); }} className="flex-1 rounded-full bg-white/5 border border-white/10 py-2.5 font-medium hover:bg-white/10 transition-colors">Cancelar</button>
              <button onClick={() => setConfirm(true)} data-testid="condo-review-btn" className="flex-1 rounded-full brand-gradient py-2.5 text-white font-semibold active:scale-95 transition-transform">Revisar dados</button>
            </div>
          </div>
        )}
      </div>

      <ThemeToggle />

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-5">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirm(false)} />
          <div className="relative w-full max-w-md glass-strong border border-white/10 rounded-3xl p-6 float-in">
            <div className="flex items-center justify-between mb-3">
              <p className="font-display font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> Confirmar alteração</p>
              <button onClick={() => setConfirm(false)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Revise os dados antes de confirmar. Esta ação atualizará o perfil oficial do condomínio.</p>
            <div className="rounded-2xl bg-white/5 p-4 text-sm space-y-1 mb-4">
              <p className="font-semibold">{f.name}</p>
              <p className="text-muted-foreground">{f.address}, {f.number}{f.complement ? ` · ${f.complement}` : ""}</p>
              <p className="text-muted-foreground">{f.neighborhood} — {f.city}/{f.state} · CEP {f.cep}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setConfirm(false)} className="flex-1 rounded-full bg-white/5 border border-white/10 py-2.5 font-medium hover:bg-white/10 transition-colors">Voltar</button>
              <button onClick={save} disabled={saving} data-testid="condo-confirm-btn" className="flex-1 rounded-full brand-gradient py-2.5 text-white font-semibold active:scale-95 transition-transform disabled:opacity-60">{saving ? "Salvando..." : "Confirmar alteração"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
